export const PAYMENT_REQUEST_STATUS = "solicitar pagamento";

export const PAYMENT_EMAIL_FAILED_FIELD = "paymentEmailFailed";
export const PAYMENT_EMAIL_FAILED_AT_FIELD = "paymentEmailFailedAt";
export const PAYMENT_EMAIL_FAILED_REASON_FIELD = "paymentEmailFailedReason";

/** Mensagem segura para a UI — sem detalhes internos de SMTP. */
export const PAYMENT_EMAIL_SMTP_FLAG_MESSAGE =
  "O e-mail não foi enviado devido à instabilidade do SMTP.";

export const PAYMENT_EMAIL_RESEND_BUTTON_LABEL = "Reenviar";

export function isPaymentRequestStatus(status) {
  return String(status || "").toLowerCase().trim() === PAYMENT_REQUEST_STATUS;
}

export function hasPaymentEmailFailure(payment) {
  return payment?.[PAYMENT_EMAIL_FAILED_FIELD] === true;
}

/**
 * Remove detalhes técnicos/sensíveis; a UI sempre usa a mensagem genérica.
 */
export function sanitizeEmailFailureReason(reason) {
  if (reason == null) return PAYMENT_EMAIL_SMTP_FLAG_MESSAGE;
  if (typeof reason === "string") {
    const trimmed = reason.trim();
    if (!trimmed) return PAYMENT_EMAIL_SMTP_FLAG_MESSAGE;
    const lower = trimmed.toLowerCase();
    if (
      lower.includes("password") ||
      lower.includes("senha") ||
      lower.includes("secret") ||
      lower.includes("credential") ||
      lower.includes("smtp://") ||
      lower.includes("authorization")
    ) {
      return PAYMENT_EMAIL_SMTP_FLAG_MESSAGE;
    }
    return PAYMENT_EMAIL_SMTP_FLAG_MESSAGE;
  }
  return PAYMENT_EMAIL_SMTP_FLAG_MESSAGE;
}

export function buildEmailFailurePersistence(reason, at = new Date()) {
  return {
    [PAYMENT_EMAIL_FAILED_FIELD]: true,
    [PAYMENT_EMAIL_FAILED_AT_FIELD]:
      at instanceof Date ? at.toISOString() : String(at),
    [PAYMENT_EMAIL_FAILED_REASON_FIELD]: sanitizeEmailFailureReason(reason),
  };
}

export function buildEmailSuccessPersistence() {
  return {
    [PAYMENT_EMAIL_FAILED_FIELD]: false,
    [PAYMENT_EMAIL_FAILED_AT_FIELD]: null,
    [PAYMENT_EMAIL_FAILED_REASON_FIELD]: null,
  };
}

export function resolvePaymentRequestEmailSubject({
  previousStatus,
  houveMudancaValores = false,
  obsFoiAlterada = false,
} = {}) {
  const isErrata =
    isPaymentRequestStatus(previousStatus) &&
    (houveMudancaValores || obsFoiAlterada);
  return isErrata
    ? "Errata! Solicitação de Pagamento"
    : "Solicitação de Pagamento";
}

/**
 * Salva o pagamento primeiro; o e-mail é secundário.
 * Falha de SMTP nunca lança nem desfaz o save.
 */
export async function savePaymentThenSendRequestEmail({
  savePayment,
  sendEmail,
  persistEmailFailure,
  persistEmailSuccess,
  shouldSendEmail = true,
}) {
  await savePayment();

  if (!shouldSendEmail) {
    return {
      paymentSaved: true,
      emailSent: null,
      emailFailed: false,
    };
  }

  let emailResult;
  try {
    emailResult = await sendEmail();
  } catch (err) {
    emailResult = {
      sent: false,
      reason: err?.message || PAYMENT_EMAIL_SMTP_FLAG_MESSAGE,
    };
  }

  if (emailResult?.sent === true) {
    if (typeof persistEmailSuccess === "function") {
      await persistEmailSuccess();
    }
    return {
      paymentSaved: true,
      emailSent: true,
      emailFailed: false,
    };
  }

  const reason = sanitizeEmailFailureReason(
    emailResult?.reason || PAYMENT_EMAIL_SMTP_FLAG_MESSAGE,
  );

  if (typeof persistEmailFailure === "function") {
    await persistEmailFailure(reason);
  }

  return {
    paymentSaved: true,
    emailSent: false,
    emailFailed: true,
    emailReason: reason,
  };
}

/**
 * Reenvio: não limpa a flag no início; só remove após sucesso confirmado.
 */
export async function resendPaymentRequestEmail({
  sendEmail,
  persistEmailFailure,
  persistEmailSuccess,
}) {
  let emailResult;
  try {
    emailResult = await sendEmail();
  } catch (err) {
    emailResult = {
      sent: false,
      reason: err?.message || PAYMENT_EMAIL_SMTP_FLAG_MESSAGE,
    };
  }

  if (emailResult?.sent === true) {
    if (typeof persistEmailSuccess === "function") {
      await persistEmailSuccess();
    }
    return {
      emailSent: true,
      emailFailed: false,
    };
  }

  const reason = sanitizeEmailFailureReason(
    emailResult?.reason || PAYMENT_EMAIL_SMTP_FLAG_MESSAGE,
  );

  if (typeof persistEmailFailure === "function") {
    await persistEmailFailure(reason);
  }

  return {
    emailSent: false,
    emailFailed: true,
    emailReason: reason,
  };
}
